/**
 * Email Typo Detection Utility
 *
 * Detects common email domain typos and suggests corrections.
 * Prevents lost leads from typos like "gmial.com" → "gmail.com"
 */

// Common domain typos mapped to correct domains
const DOMAIN_TYPOS: Record<string, string> = {
  // Gmail typos
  'gmial.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gamil.com': 'gmail.com',
  'gmil.com': 'gmail.com',
  'gnail.com': 'gmail.com',
  'gmaill.com': 'gmail.com',
  'gmaiil.com': 'gmail.com',
  'gmal.com': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmail.cm': 'gmail.com',
  'gmail.om': 'gmail.com',
  'gmail.con': 'gmail.com',
  'gmail.cpm': 'gmail.com',
  'gmail.vom': 'gmail.com',
  'g]mail.com': 'gmail.com',

  // Yahoo typos
  'yahooo.com': 'yahoo.com',
  'yaho.com': 'yahoo.com',
  'yahho.com': 'yahoo.com',
  'yahool.com': 'yahoo.com',
  'yahoo.co': 'yahoo.com',
  'yahoo.cm': 'yahoo.com',
  'yahoo.con': 'yahoo.com',
  'yhaoo.com': 'yahoo.com',

  // Hotmail typos
  'hotmal.com': 'hotmail.com',
  'hotmial.com': 'hotmail.com',
  'hotmil.com': 'hotmail.com',
  'hotmaill.com': 'hotmail.com',
  'hotmail.co': 'hotmail.com',
  'hotmail.cm': 'hotmail.com',
  'hotmail.con': 'hotmail.com',
  'hotamil.com': 'hotmail.com',
  'hotmailcom': 'hotmail.com',

  // Outlook typos
  'outloo.com': 'outlook.com',
  'outlok.com': 'outlook.com',
  'outllook.com': 'outlook.com',
  'outlookk.com': 'outlook.com',
  'outlook.co': 'outlook.com',
  'outlook.cm': 'outlook.com',
  'outlook.con': 'outlook.com',
  'outloook.com': 'outlook.com',
  'oulook.com': 'outlook.com',

  // iCloud typos
  'icoud.com': 'icloud.com',
  'iclod.com': 'icloud.com',
  'iclooud.com': 'icloud.com',
  'icloud.co': 'icloud.com',
  'icloud.cm': 'icloud.com',
  'icloud.con': 'icloud.com',

  // AOL typos
  'aoll.com': 'aol.com',
  'aol.co': 'aol.com',
  'aol.cm': 'aol.com',
  'aol.con': 'aol.com',

  // Comcast typos
  'comast.net': 'comcast.net',
  'comcsat.net': 'comcast.net',
  'comcast.ner': 'comcast.net',
  'comcast.met': 'comcast.net',

  // Common TLD typos (applied to any domain)
  // These are handled separately in the function
}

// Common TLD typos
const TLD_TYPOS: Record<string, string> = {
  '.con': '.com',
  '.cpm': '.com',
  '.vom': '.com',
  '.cm': '.com',
  '.co': '.com', // Only suggest if not a valid .co domain
  '.ner': '.net',
  '.met': '.net',
  '.nt': '.net',
  '.ogr': '.org',
  '.og': '.org',
}

// Valid .co domains that shouldn't be corrected
const VALID_CO_DOMAINS = [
  'twitter.co',
  'google.co',
  'amazon.co',
  't.co',
]

export interface EmailSuggestion {
  original: string
  suggested: string
  domain: string
  correctedDomain: string
}

/**
 * Check if an email has a common typo and suggest a correction
 *
 * @param email - The email address to check
 * @returns Suggestion object if typo detected, null otherwise
 */
export function detectEmailTypo(email: string): EmailSuggestion | null {
  if (!email || !email.includes('@')) {
    return null
  }

  const [localPart, domain] = email.toLowerCase().split('@')

  if (!domain) {
    return null
  }

  // First, check for exact domain matches in our typo map
  if (DOMAIN_TYPOS[domain]) {
    const correctedDomain = DOMAIN_TYPOS[domain]
    return {
      original: email,
      suggested: `${localPart}@${correctedDomain}`,
      domain,
      correctedDomain,
    }
  }

  // Check for TLD typos (e.g., .con instead of .com)
  for (const [typo, correction] of Object.entries(TLD_TYPOS)) {
    if (domain.endsWith(typo)) {
      // Special case: don't "correct" valid .co domains
      if (typo === '.co' && VALID_CO_DOMAINS.some(valid => domain.startsWith(valid.split('.')[0]))) {
        continue
      }

      const baseDomain = domain.slice(0, -typo.length)
      const correctedDomain = baseDomain + correction

      return {
        original: email,
        suggested: `${localPart}@${correctedDomain}`,
        domain,
        correctedDomain,
      }
    }
  }

  return null
}

/**
 * Get just the suggested email string, or null if no typo
 */
export function getSuggestedEmail(email: string): string | null {
  const suggestion = detectEmailTypo(email)
  return suggestion?.suggested || null
}

/**
 * Check if email domain looks suspicious (not necessarily a typo)
 * Returns true for domains that are unusual but not necessarily wrong
 */
export function isUnusualDomain(email: string): boolean {
  if (!email || !email.includes('@')) {
    return false
  }

  const domain = email.split('@')[1]?.toLowerCase()
  if (!domain) {
    return false
  }

  // Common legitimate domains
  const commonDomains = [
    'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com',
    'icloud.com', 'aol.com', 'protonmail.com', 'mail.com',
    'msn.com', 'live.com', 'comcast.net', 'verizon.net',
    'att.net', 'sbcglobal.net', 'cox.net', 'charter.net',
    'earthlink.net', 'me.com', 'mac.com',
  ]

  // If it's a common domain, not unusual
  if (commonDomains.includes(domain)) {
    return false
  }

  // If it has a reasonable TLD, probably fine
  const validTlds = ['.com', '.net', '.org', '.edu', '.gov', '.io', '.co', '.us', '.uk']
  if (validTlds.some(tld => domain.endsWith(tld))) {
    return false
  }

  return true
}
